// Copyright (c) Microsoft. All rights reserved.

import {
    Hamburger,
    makeStyles,
    SelectTabEventHandler,
    shorthands,
    Tab,
    TabList,
    TabValue,
    tokens,
    Tooltip,
    useRestoreFocusTarget,
} from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { FeatureKeys } from '../../redux/features/app/AppState';
import { Alerts } from '../shared/Alerts';
import { ChatRoom } from './ChatRoom';
import { ShareBotMenu } from './controls/ShareBotMenu';
import { Breakpoints, customTokens } from '../../styles';
import { DocumentsTab } from './tabs/DocumentsTab';
import { AgentSelectListItem } from '../../libs/services/AgentService';
import { AgentList } from '../header/AgentList';
import { UserSettingsMenu } from '../header/UserSettingsMenu';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        backgroundColor: customTokens.colorChatRoomBackground,
        ...Breakpoints.small({
            justifyContent: 'space-between',
        }),
    },
    header: {
        ...shorthands.borderBottom('1px', 'solid', customTokens.colorBorder),
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
        backgroundColor: customTokens.colorChatRoomHeaderBackgroud,
        display: 'flex',
        flexDirection: 'row',
        boxSizing: 'border-box',
        width: '100%',
        justifyContent: 'space-between',
        padding: '0 16px',
        height: '64px',
        ...Breakpoints.small({
            padding: '0 8px',
            justifyContent: 'flex-start',
        }),
    },
    title: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        ...Breakpoints.small({
            marginRight: 'auto',
        }),
    },
    actions: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
    },
    date: {
        ...Breakpoints.small({
            display: 'none',
        }),
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
    },
    popoverHeader: {
        ...shorthands.margin('0'),
        paddingBottom: tokens.spacingVerticalXXS,
        fontStyle: 'normal',
        fontWeight: '600',
    },
    popover: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        ...shorthands.padding(tokens.spacingVerticalXXL),
        ...shorthands.gap(tokens.spacingVerticalMNudge),
        width: '398px',
    },
    input: {
        width: '100%',
    },
    buttons: {
        display: 'flex',
        alignSelf: 'end',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    alerts: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.margin(0, '72px'),
    },
    agent: {
        display: 'flex',
        height: '40px',
        maxWidth: '120px',
        padding: '10px 8px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        borderRadius: '8px',
        ...shorthands.border('1px', 'solid', customTokens.colorBorder),
        boxShadow: tokens.shadow4,
        ...Breakpoints.small({
            display: 'none',
        }),
    },
    newChat: {
        fontSize: '14px',
        fontWeight: '500',
        lineHeight: '22px',
        color: customTokens.colorCustomText,
    },
    content: {
        display: 'none',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        ...Breakpoints.small({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px',
        }),
    },
    actionMenu: {
        borderRadius: '8px',
        ...shorthands.padding(tokens.spacingVerticalL),
        lineHeight: '22px',
    },
    editButton: {
        padding: 0,
    },
    menuItem: {
        padding: 0,
        minHeight: '22px',
    },
    menuSeperator: {
        marginBottom: tokens.spacingVerticalM,
        marginTop: tokens.spacingVerticalM,
        backgroundColor: customTokens.colorSeperator,
        height: '1px',
    },
});

interface ChatWindowBaseProps {
    selectedAgentId: string;
    setSelectedAgentId: (id: string) => void;
    agents: AgentSelectListItem[];
    onMessageSent?: () => void;
}

interface IListItemActionsProps {
    setIsOpen: () => void;
    onEditTitleClick: () => void;
    panelOpen?: boolean;
    setPanelOpen?: (open: boolean | ((prev: boolean) => boolean)) => void;
}

type ChatWindowProps = ChatWindowBaseProps & IListItemActionsProps;

export const ChatWindow: React.FC<ChatWindowProps> = ({
    selectedAgentId,
    setSelectedAgentId,
    agents,
    onMessageSent,
    setIsOpen,
    panelOpen,
    setPanelOpen,
}) => {
    const classes = useClasses();
    const { features } = useAppSelector((state: RootState) => state.app);
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const showShareBotMenu = features[FeatureKeys.BotAsDocs].enabled;
    const chatName = conversations[selectedId].title;
    const restoreFocusTargetAttributes = useRestoreFocusTarget();
    const [selectedTab, setSelectedTab] = React.useState<TabValue>('chat');
    const [isSuperAgent, setIsSuperAgent] = useState<boolean>(false);
    const [isDocumentUploadEnabled, setIsDocumentUploadEnabled] = useState<boolean>(false);

    useEffect(() => {
        setIsSuperAgent(agents.length > 0 && agents[0].value === selectedAgentId);
    }, [agents, selectedAgentId]);

    useEffect(() => {
        setIsDocumentUploadEnabled(
            agents.length > 0 && agents.find((a) => a.value === selectedAgentId)?.isDocumentUploadEnabled === true,
        );
    }, [agents, selectedAgentId, isDocumentUploadEnabled]);

    const onTabSelect: SelectTabEventHandler = (_event, data) => {
        const tabValue = data.value;
        setSelectedTab(tabValue);
    };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.content}>
                    <Tooltip content="Toggle navigation pane" relationship="label">
                        <div>
                            <Hamburger onClick={setIsOpen} {...restoreFocusTargetAttributes} aria-expanded={true} />
                        </div>
                    </Tooltip>
                </div>
                <div className={classes.title}>
                    <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
                        <Tab data-testid="chatTab" id="chat" value="chat" aria-label="Chat Tab" title="Chat Tab">
                            Sohbet
                        </Tab>
                        {/* <Tab
                            data-testid="documentsTab"
                            id="documents"
                            value="documents"
                            aria-label="Documents Tab"
                            title="Documents Tab"
                            disabled
                        >
                            Dokümanlar
                        </Tab> */}
                    </TabList>
                    <div>
                        <AgentList
                            selectedAgentId={selectedAgentId}
                            setSelectedAgentId={setSelectedAgentId}
                            agents={agents}
                        />
                    </div>
                    <div className={classes.actions}>
                        {/* <Menu>
                            <MenuTrigger disableButtonEnhancement>
                                <Button
                                    data-testid="moreButton"
                                    icon={<MoreHorizontalRegular />}
                                    appearance="transparent"
                                    onClick={() => {
                                    }}
                                    />
                                </MenuTrigger>
                                <MenuPopover className={classes.actionMenu}>
                                    <MenuList>
                                        <MenuItem data-testid="addNewBotMenuItem" onClick={onEditTitleClick} className={classes.menuItem}>
                                            <Button
                                                icon={<Edit />}
                                                appearance="transparent"
                                                className={classes.editButton}
                                                aria-label="Yeniden isimlendir"
                                                onClick={onEditTitleClick}
                                                data-testid="editChatTitleButtonSimplified"
                                            >Yeniden isimlendir</Button>
                                        </MenuItem>
                                        <div className={classes.menuSeperator}></div>
                                        <MenuItem data-testid="addNewBotMenuItem" className={classes.menuItem}>
                                            <DeleteChatDialog chatId={selectedId} />
                                        </MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu> */}
                    </div>
                </div>
                <div className={classes.controls}>
                    <UserSettingsMenu panelOpen={panelOpen} setPanelOpen={setPanelOpen} />
                    {showShareBotMenu && (
                        <div>
                            <ShareBotMenu chatId={selectedId} chatTitle={chatName} />
                        </div>
                    )}
                </div>
            </div>
            {selectedTab === 'chat' && (
                <ChatRoom
                    selectedAgentId={selectedAgentId}
                    onMessageSent={onMessageSent}
                    isSuperAgent={isSuperAgent}
                    isDocumentUploadEnabled={isDocumentUploadEnabled}
                />
            )}
            {selectedTab === 'documents' && <DocumentsTab />}
            {selectedTab !== 'chat' && (
                <div className={classes.alerts}>
                    <Alerts />
                </div>
            )}
        </div>
    );
};
